"use client"

import { useState, useEffect, useMemo } from "react"
import { Link } from "react-router-dom"
import { useParams, useNavigate } from "react-router-dom"
import { useApp } from "../contexts/AppContext"
import { BiSave, BiExport, BiTrash, BiArrowBack, BiFile, BiFolder, BiX, BiArrowToTop, BiArrowToBottom, BiListUl, BiRefresh } from "react-icons/bi"
import { motion, AnimatePresence } from "framer-motion"
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';

import HeaderAlert from "../components/ui/HeaderAlert"
import ConfirmAlert from "../components/alerts/ConfirmAlert"
import FieldsForm from "../components/forms/FieldsForm"

const FIELD_TYPES = [
  { value: "TEXT", label: "Texte" },
  { value: "TEXTAREA", label: "Texte long" },
  { value: "NUMBER", label: "Entier" },
  { value: "DOUBLE", label: "Réel" },
  { value: "SELECT", label: "Choix" },
  { value: "DATE", label: "Date" },

  { value: "CHECKBOX", label: "Case à cocher" },
  { value: "FILE", label: "Fichier" },
  { value: "RADIO", label: "Radio" },
  { value: "RANGE", label: "Barre de défilement" },
  { value: "TIME", label: "Heure" },
  { value: "DATETIME", label: "Date et heure" },
  { value: "COLOR", label: "Couleur" },
]

export default function FeuilleFieldPage() {
  const { feuilleId } = useParams()
  const navigate = useNavigate()

  const {
    getFeuille,
    getFeuilleLignes,
    addLignesAPI,
    updateLigneAPI,
    deleteLignesAPI,
    loading,
    error,
    success,
    clearError,
    clearSuccess,
  } = useApp()

  const [fields, setFields] = useState([])
  const [errors, setErrors] = useState({})
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteFieldKey, setDeleteFieldKey] = useState()

  const projetId = localStorage.getItem("selec-projet")
  const classeurId = localStorage.getItem("selec-classeur")
  const feuille = useMemo(() => { return getFeuille(feuilleId) }, [feuilleId, getFeuille])
  const feuillechamps = getFeuilleLignes(feuilleId)

  useEffect(() => {
    if (feuilleId && feuille !== null) {
      const champsWithKey = feuillechamps.map((champ) => ({ ...champ, key_ligne: champ.code_feuille_ligne }))
      setFields(champsWithKey)
    }
  }, [feuilleId, getFeuille, deleteFieldKey])


  const handleRemoveField = (fieldKey) => {
    setDeleteFieldKey(fieldKey)
    setShowDeleteConfirm(true)
  }

  const confirmRemoveField = async () => {
    try {
      const fieldDeleted = fields.filter((f) => deleteFieldKey == f.key_ligne)
      const dataDelete = { code_feuille: feuille.code_feuille, ids: fieldDeleted.map((f) => f.code_feuille_ligne) }

      await deleteLignesAPI(dataDelete)
      setShowDeleteConfirm(false)
      setDeleteFieldKey(null)
    } catch (err) {
      // Error handled by AppContext
    }
  }

  const validateForm = (field) => {
    const newErrors = {}
    if (!field.nom_ligne.trim()) {
      newErrors.nom_ligne = "Le nom technique est obligatoire"
    }
    if (!field.libelle_ligne.trim()) {
      newErrors.libelle_ligne = "Le libellé est obligatoire"
    }
    return newErrors
  }

  const handleFieldChange = (fieldId, property, value) => {
    setFields((prevFields) => prevFields.map((f) => (f.key_ligne === fieldId ? { ...f, [property]: value } : f)))

    if (errors[fieldId] && errors[fieldId][property] && value.trim()) {
      setErrors((prev) => {
        const newErr = { ...prev }
        delete newErr[fieldId][property]
        if (Object.keys(newErr[fieldId]).length === 0) {
          delete newErr[fieldId]
        }
        return newErr
      })
    }
  }

  const handleSaveChamps = async () => {
    let hasErrors = false
    let allErrors = {}
    let colToAdd = []
    let colToUpdate = []

    fields.forEach((field) => {
      const fieldErrors = validateForm(field)

      if (Object.keys(fieldErrors).length > 0) {
        hasErrors = true
        allErrors[field.key_ligne] = fieldErrors
      } else {
        setErrors((prev) => {
          const newErr = { ...prev }
          if (newErr[field.key_ligne] && Object.keys(newErr[field.key_ligne]).length === 0) delete newErr[field.key_ligne]
          return newErr
        })

        if (field.code_feuille_ligne && field.code_feuille_ligne !== "") {
          delete field.code_feuille
          colToUpdate.push(field)
        } else {
          colToAdd.push(field)
        }
      }
    })

    if (hasErrors) {
      setErrors(allErrors)
      return
    }

    try {
      const promises = []
      if (colToAdd.length > 0) promises.push(addLignesAPI({ code_feuille: feuille.code_feuille, colonnes: colToAdd }))
      if (colToUpdate.length > 0) promises.push(updateLigneAPI({ code_feuille: feuille.code_feuille, colonnes: colToUpdate }))

      await Promise.all(promises)
      navigate(`/classeur/${feuille.code_classeur}`)
    } catch (err) {
      console.error("Erreur lors de l'enregistrement :", err)
    }
  }

  const handleExportCanevas = () => {
    const headers = fields.map((c) => c.nom_ligne)
    const csvContent = [
      headers.join(';'),
      headers.map(() => '').join(';')
    ].join('\n')

    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', `canevas_${feuille.nom_feuille || 'feuille'}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }


  const handleMoveField = (key, direction) => {
    setFields((prevFields) => {
      const index = prevFields.findIndex(f => f.key_ligne === key)
      if (index === -1) return prevFields

      let newIndex = direction === "up" ? index - 1 : index + 1
      if (newIndex < 0 || newIndex >= prevFields.length) return prevFields

      const updated = [...prevFields]
      const [movedItem] = updated.splice(index, 1)
      updated.splice(newIndex, 0, movedItem)

      return updated.map((f, idx) => ({ ...f, rang: idx + 1 }))
    })
  }

  const headerVariants = {
    hidden: { y: -50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
  }



  if (!feuille) {
    return (
      <div className="m-3 p-3 border shadow-sm rounded-1 bg-white" style={{ minHeight: "95vh" }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-5"
        >
          <BiFile className="display-1 text-danger mb-3" />
          <h3 className="text-danger">Feuille non trouvée</h3>
          <p className="text-muted mb-4">La feuille demandée n'existe pas ou a été supprimée.</p>
          <button className="btn btn-primary" onClick={() => navigate(`/classeur/${classeurId}`)}>
            <BiArrowBack className="me-1" />
            Retour au classeur
          </button>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="m-3 p-3 border shadow-sm rounded-1 bg-white" style={{ minHeight: "95vh" }}>
      <HeaderAlert error={error} success={success} clearError={clearError} clearSuccess={clearSuccess} />

      {/* En-tête */}
      <motion.div variants={headerVariants} initial="hidden" animate="visible" className="mb-4">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="flex-grow-1">
            <nav aria-label="breadcrumb">
              <motion.ol
                className="breadcrumb mb-2"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}>
                <li className="breadcrumb-item">
                  <Link to={`/projet/${projetId}`} className="text-decoration-none d-flex align-items-center">
                    <BiFolder className="me-1" /> Classeur
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={`/classeur/${feuille.code_classeur}`} className="text-decoration-none d-flex align-items-center">
                    <BiFile className="me-1" /> Feuille
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">Éditeur de champs</li>
              </motion.ol>
            </nav>
            <h5 className="mb-0">Éditeur de champs - {feuille.nom_feuille}</h5>
          </div>

          <div className="d-flex gap-2">
            <motion.button
              onClick={() => window.location.reload()}
              title="Rafraichir"
              className="btn btn-sm btn-outline-secondary d-flex align-items-center p-1 rounded-circle"
              disabled={loading}>
              <BiRefresh className="fs-5" />
            </motion.button>

            <button className="btn btn-sm btn-success px-2 d-flex align-items-center"
              onClick={handleSaveChamps}
              disabled={loading}>
              <BiSave className="me-1" />
              Sauvegarder
            </button>

            <button className="btn btn-sm btn-secondary px-2 d-flex align-items-center"
              onClick={() => navigate(`/classeur/${feuille.code_classeur}`)}
              disabled={loading}>
              <BiX className="me-1" /> Annuler
            </button>
          </div>
        </div>
      </motion.div>

      <div className="row">
        <div className="col-md-8">
          <motion.div
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="card rounded-1 shadow-sm h-100" style={{ minHeight: "500px" }}>
            <div className="card-header bg-light d-flex justify-content-between align-items-center py-1">
              <h6 className="mb-0">Champs existants</h6>

              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-light px-2 d-flex align-items-center"
                  onClick={handleExportCanevas}
                  disabled={loading}>
                  <BiExport className="me-1" />
                  Exporter Canevas
                </button>
              </div>
            </div>
            <div className="card-body position-relative">
              {!loading && fields.length === 0 ? (
                <div className="text-center py-4">
                  <BiFile className="display-4 text-muted mb-3" />
                  <p className="text-muted">Aucun champ défini pour cette feuille.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover small">
                    <thead className="table-light">
                      <tr>
                        <th>Nom technique</th>
                        <th>Libellé</th>
                        <th>Type</th>
                        <th>Requis</th>
                        <th>Visible</th>
                        <th>Mobile</th>
                        <th>Rang</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <AnimatePresence>
                        {fields
                          .slice().sort((a, b) => a.rang - b.rang)
                          .map((field) => (
                            <motion.tr
                              key={field.key_ligne}
                              layout
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -20 }}
                              transition={{ duration: 0.25 }}
                            >
                              <td>
                                <input
                                  type="text"
                                  className={`form-control form-control-sm ${errors[field.key_ligne]?.nom_champ ? "is-invalid" : ""}`}
                                  value={field.nom_ligne}
                                  onChange={(e) => handleFieldChange(field.key_ligne, "nom_ligne", e.target.value)}
                                />
                                {errors[field.key_ligne]?.nom_ligne && (
                                  <div className="invalid-feedback"> {errors[field.key_ligne].nom_ligne}</div>
                                )}
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className={`form-control form-control-sm ${errors[field.key_ligne]?.libelle_champ ? "is-invalid" : ""}`}
                                  value={field.libelle_ligne}
                                  onChange={(e) => handleFieldChange(field.key_ligne, "libelle_ligne", e.target.value)}
                                />
                                {errors[field.key_ligne]?.libelle_ligne && (
                                  <div className="invalid-feedback"> {errors[field.key_ligne].libelle_ligne}</div>
                                )}
                              </td>
                              <td>
                                <select
                                  className="form-select form-select-sm"
                                  value={field.type_ligne}
                                  onChange={(e) => handleFieldChange(field.key_ligne, "type_ligne", e.target.value)}
                                >
                                  {FIELD_TYPES.map((type) => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <select
                                  className="form-select form-select-sm"
                                  value={field.requis}
                                  onChange={(e) => handleFieldChange(field.key_ligne, "requis", e.target.value)}>
                                  <option value="Oui">Oui</option>
                                  <option value="Non">Non</option>
                                </select>
                              </td>
                              <td>
                                <select
                                  className="form-select form-select-sm"
                                  value={field.afficher}
                                  onChange={(e) => handleFieldChange(field.key_ligne, "afficher", e.target.value)}>
                                  <option value="Oui">Oui</option>
                                  <option value="Non">Non</option>
                                </select>
                              </td>
                              <td>
                                <select
                                  className="form-select form-select-sm"
                                  value={field.mobile}
                                  onChange={(e) => handleFieldChange(field.key_ligne, "mobile", e.target.value)}>
                                  <option value="Oui">Oui</option>
                                  <option value="Non">Non</option>
                                </select>
                              </td>
                              <td>
                                <div className="d-flex gap-1">
                                  <button className="btn btn-sm btn-light text-info p-1 d-flex align-items-center"
                                    onClick={() => handleMoveField(field.key_ligne, "up")}>
                                    <BiArrowToTop />
                                  </button>
                                  <button className="btn btn-sm btn-light text-info p-1 d-flex align-items-center"
                                    onClick={() => handleMoveField(field.key_ligne, "down")}>
                                    <BiArrowToBottom />
                                  </button>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center justify-content-end gap-1">
                                  {field.type_ligne?.toLowerCase() === "select" &&
                                    <OverlayTrigger
                                      key={field.key_ligne}
                                      placement="bottom"
                                      trigger="click"
                                      rootClose
                                      delay={{ show: 200, hide: 50 }}
                                      overlay={
                                        <Popover className="rounded-1">
                                          <Popover.Body className="py-1 px-2">
                                            {field.options_ligne ? (
                                              <span className="text-muted small">{field.options_ligne}</span>) : (
                                              <span className="text-muted small">Aucune option</span>
                                            )}
                                          </Popover.Body>
                                        </Popover>
                                      }>
                                      <button className="btn btn-sm btn-light text-secondary p-1 d-flex align-items-center" 
                                      title="Options du champ"
                                      disabled={loading}>
                                        <BiListUl />
                                      </button>
                                    </OverlayTrigger>}

                                  <button className="btn btn-sm btn-light text-danger p-1 d-flex align-items-center"
                                    onClick={() => handleRemoveField(field.key_ligne)}
                                    disabled={loading}>
                                    <BiTrash />
                                  </button>
                                </div>
                              </td>
                            </motion.tr>
                          ))}
                      </AnimatePresence>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Indicateur de chargement */}
              {loading && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75 rounded">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        <div className="col-md-4">
          <motion.div
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="card rounded-1 shadow-sm">
            <div className="card-header bg-light">
              <h6 className="mb-0 pt-1">Ajouter un champ</h6>
            </div>

            <div className="card-body">
              <FieldsForm
                fields={fields}
                setFields={setFields}
                errors={errors}
                setErrors={setErrors}
                loading={loading}
                validateForm={validateForm}
                FIELD_TYPES={FIELD_TYPES} />
            </div>
          </motion.div>
        </div>
      </div>

      <ConfirmAlert
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmRemoveField}
        title="Supprimer le champ"
        message={`Êtes-vous sûr de vouloir supprimer le champ "${fields.find((f) => f.key_ligne === deleteFieldKey)?.libelle_champ
          }" ? Cette action est irréversible et peut affecter les données existantes.`}
        confirmText="Supprimer"
        variant="danger"
      />
    </div>
  )
}
